import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import BEM from '../../../../common/bem';
import { ReactComponent as DndStatusSvg } from '../../../../common/images/dnd-status.svg';
import { ReactComponent as ArrowDownSvg } from '../../../../common/images/arrow-down.svg';
import { ReactComponent as ArrowUpSvg } from '../../../../common/images/arrow-up.svg';
import { ReactComponent as AddLoadingSvg } from '../../../../common/images/add-loading.svg';
import { Group, User, Conversation } from '../../../../types';
import { mobxInjectSelect } from '../../../../common/utils';
import { DelegationMemberInfo } from '../../../../common/components';
import {
  fetchLastAutoForwardData,
  fetchAutoForwardDataForRole,
  isFulfilledResult,
  ForwardedEntity,
} from '../../../../common/utils/autoForward';

const classes = BEM.with('DelegationBanner');

const DelegationText = ({ delegationText }: { delegationText: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const hasOverflow = ref.current.scrollHeight > ref.current.clientHeight;
      setIsOverflowing(hasOverflow);
    }
    setIsExpanded(false);
  }, [delegationText]);

  const handleClick = () => {
    if (isOverflowing || isExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className={classes('delegation-text', { isExpanded, isOverflowing })}
      title={delegationText}
      data-test-id="delegation-text"
    >
      {delegationText}
    </div>
  );
};

type DelegationBannerProps = {
  closeInfoPane: () => void;
  currentConversation: Conversation;
  currentOrganizationId: string;
  currentUserId: string;
  getConversationType?: (conversation: Conversation) => string;
  infoPane: { type: string } | null;
  initializeGroupEditor: ({ group }: { group: Group }) => void;
  logGainsightAnalytics?: (params: {
    tracker: { name: string; props?: Record<string, unknown> };
  }) => void;
  subscribeUser: (userId: string) => void;
  syncGroupMembers: () => void;
  unsubscribeUser: (userId: string) => void;
  realMembers: string[];
};

const DelegationBannerComponent = ({
  closeInfoPane,
  currentConversation,
  currentOrganizationId,
  currentUserId,
  getConversationType,
  infoPane,
  initializeGroupEditor,
  logGainsightAnalytics,
  subscribeUser,
  syncGroupMembers,
  unsubscribeUser,
  realMembers,
}: DelegationBannerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoForwardData, setAutoForwardData] = useState<
    Array<(ForwardedEntity | undefined)[] | undefined>
  >([]);
  const [roleAutoForwardData, setRoleAutoForwardData] = useState<
    Array<(ForwardedEntity | undefined)[] | undefined>
  >([]);
  const [isLoadingAutoForward, setIsLoadingAutoForward] = useState(false);

  const group = currentConversation?.counterParty as Group;

  const members = useMemo(() => group?.members || [], [group?.members]);
  const memberIds = members.map((member) => member.botUser?.id || member.id) || [];

  const memberDndHash =
    members
      ?.map((m) => `${m.id}:${!!m.dnd}`)
      .sort()
      .join('|') || '';

  const unavailableMembers = useMemo(
    () => members?.filter((member) => member.dnd && member.id !== currentUserId) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberDndHash, currentUserId]
  );

  const memberRolesHash =
    unavailableMembers
      ?.map((m) => `${m.id}:${m.roles?.length || 0}`)
      .sort()
      .join('|') || '';

  const unavailableRoles = useMemo(() => {
    const memberBotUserIds = new Set(members?.map((m) => m.botUser?.id || m.id) || []);

    return unavailableMembers.flatMap((member) => {
      if (!member.roles) return [];
      return member.roles
        .filter((role) => role.botUserId && memberBotUserIds.has(role.botUserId))
        .map((role) => ({ role, owner: member }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unavailableMembers, members, memberRolesHash]);

  const filteredMembers = useMemo(() => {
    const realMembersSet = new Set(realMembers);
    return unavailableMembers.filter((member) => realMembersSet.has(member.id));
  }, [unavailableMembers, realMembers]);

  useEffect(() => {
    members.forEach((member) => {
      if (member.id !== currentUserId) {
        subscribeUser(member.id);
      }
    });

    return () => {
      members.forEach((member) => {
        if (member.id !== currentUserId) {
          unsubscribeUser(member.id);
        }
      });
    };
  }, [group?.id, members, currentUserId, subscribeUser, unsubscribeUser]);

  const fetchAutoForwardDataForMembers = useCallback(
    async (membersToFetch: User[]) => {
      if (membersToFetch.length === 0) return [];

      try {
        const userResults = await Promise.allSettled(
          membersToFetch.map((member) => {
            return fetchLastAutoForwardData(member, currentOrganizationId);
          })
        );

        return userResults.filter(isFulfilledResult).map((result) => result.value);
      } catch (error) {
        console.error('Failed to fetch auto-forward data:', error);
        return [];
      }
    },
    [currentOrganizationId]
  );

  const handleToggleBanner = useCallback(async () => {
    if (isLoadingAutoForward) return;

    const newExpandedState = !isExpanded;

    if (!newExpandedState) {
      if (infoPane?.type === 'AutoForwardRecipients') {
        closeInfoPane();
      }
      setIsExpanded(false);
      return;
    }

    logGainsightAnalytics?.({
      tracker: {
        name: 'Inbox | Messenger - Unavailable Banner Opened',
        props: {
          ConversationType: getConversationType?.(currentConversation) ?? 'Unknown',
        },
      },
    });
    setIsLoadingAutoForward(true);

    const fetchPromises: Promise<void>[] = [];

    if (filteredMembers.length > 0) {
      fetchPromises.push(
        fetchAutoForwardDataForMembers(filteredMembers).then((data) => {
          setAutoForwardData(data);
        })
      );
    }

    if (unavailableRoles.length > 0) {
      fetchPromises.push(
        Promise.allSettled(
          unavailableRoles.map(({ role }) =>
            fetchAutoForwardDataForRole(role, currentOrganizationId)
          )
        ).then((results) => {
          const data = results.filter(isFulfilledResult).map((result) => result.value);
          setRoleAutoForwardData(data);
        })
      );
    }

    await Promise.all(fetchPromises);
    setIsLoadingAutoForward(false);
    setIsExpanded(true);
  }, [
    isLoadingAutoForward,
    isExpanded,
    filteredMembers,
    unavailableRoles,
    fetchAutoForwardDataForMembers,
    currentOrganizationId,
    infoPane,
    closeInfoPane,
    logGainsightAnalytics,
    getConversationType,
    currentConversation,
  ]);

  useEffect(() => {
    setIsExpanded(false);
    if (group) {
      initializeGroupEditor({ group });
      syncGroupMembers();
    }
  }, [initializeGroupEditor, group, syncGroupMembers]);

  useEffect(() => {
    if (filteredMembers.length > 0) {
      fetchAutoForwardDataForMembers(filteredMembers).then((data) => {
        setAutoForwardData(data);
      });
    }
  }, [filteredMembers, fetchAutoForwardDataForMembers]);

  useEffect(() => {
    if (unavailableRoles.length > 0) {
      Promise.allSettled(
        unavailableRoles.map(({ role }) => fetchAutoForwardDataForRole(role, currentOrganizationId))
      ).then((results) => {
        const data = results.filter(isFulfilledResult).map((result) => result.value);
        setRoleAutoForwardData(data);
      });
    }
  }, [unavailableRoles, currentOrganizationId]);

  const totalUnavailableCount = useMemo(() => {
    const memberCount = filteredMembers.length;
    const rolesCount = unavailableRoles.length;
    return memberCount + rolesCount;
  }, [filteredMembers, unavailableRoles]);

  if (totalUnavailableCount === 0) return null;

  const bannerText =
    totalUnavailableCount === 1
      ? '1 person in this group is unavailable'
      : `${totalUnavailableCount} people in this group are unavailable`;

  return (
    <div className={classes()} data-test-id="delegation-banner">
      <div className={classes('delegation-status')}>
        <div className={classes('delegation-status-information')}>
          <div className={classes('delegation-column-container')}>
            <div className={classes('delegation-svg')}>
              <DndStatusSvg />
            </div>
            <DelegationText delegationText={bannerText} />
          </div>

          <div
            className={classes('delegation-column-container')}
            onClick={handleToggleBanner}
            data-test-id="delegation-banner-toggle-details"
          >
            <span>{isExpanded ? 'Hide details' : 'Show details'}</span>
            <div className={classnames(classes('delegation-svg'), classes('delegation-svg-arrow'))}>
              {isLoadingAutoForward ? (
                <AddLoadingSvg
                  className={classnames(
                    classes('delegation-loading-icon'),
                    classes('delegation-loading-icon--spinning')
                  )}
                />
              ) : isExpanded ? (
                <ArrowUpSvg className={classes('delegation-arrow-icon')} />
              ) : (
                <ArrowDownSvg className={classes('delegation-arrow-icon')} />
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className={classes('delegation-unavailable-members-wrapper')}>
            {filteredMembers.map((member, index) => (
              <div
                className={classes('delegation-unavailable-members-row')}
                key={member.id}
                data-test-id={`delegation-banner-individual-dnd-user-${index}`}
              >
                {index > 0 && (
                  <div className={classes('delegation-unavailable-members-row-separator')} />
                )}
                <div className={classes('delegation-unavailable-members-row-content')}>
                  <DelegationMemberInfo
                    member={member}
                    autoForward={autoForwardData[index]}
                    memberIds={memberIds}
                    groupId={group?.id}
                    groupType={group?.groupType}
                    groupMembers={filteredMembers}
                  />
                </div>
              </div>
            ))}
            {unavailableRoles.map(({ role }, index) => {
              const roleBotUser = role.botUser;
              if (!roleBotUser) return null;
              const overallIndex = unavailableMembers.length + index;

              if (role.members?.[0]?.dndText) {
                roleBotUser.dndText = role.members[0].dndText;
              }

              return (
                <div
                  className={classes('delegation-unavailable-members-row')}
                  key={`role-${role.id}`}
                  data-test-id={`delegation-banner-individual-dnd-role-${index}`}
                >
                  {overallIndex > 0 && (
                    <div className={classes('delegation-unavailable-members-row-separator')} />
                  )}
                  <div className={classes('delegation-unavailable-members-row-content')}>
                    <DelegationMemberInfo
                      member={roleBotUser}
                      autoForward={roleAutoForwardData[index]}
                      memberIds={memberIds}
                      groupId={group?.id}
                      groupType={group?.groupType}
                      groupMembers={filteredMembers}
                      smallOwnerName={true}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const DelegationBanner = mobxInjectSelect<{}, DelegationBannerProps>({
  conversationStore: ['currentConversation'],
  groupEditorStore: ['initializeGroupEditor', 'syncGroupMembers', 'realMembers'],
  messengerStore: ['closeInfoPane', 'currentOrganizationId', 'infoPane'],
  presenceStore: ['subscribeUser', 'unsubscribeUser'],
  sessionStore: ['currentUserId'],
  trackerStore: ['getConversationType', 'logGainsightAnalytics'],
})(observer(DelegationBannerComponent));
